// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/api/auth.api';
import { tokenManager, logoutUser } from '@/api/axiosClient';

const AuthContext = createContext(null);

// Stable device ID — generated once per browser, persisted in localStorage
function getOrCreateDeviceId() {
  const key = 'app_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── OTP step-up state ──────────────────────────────────────────────────────
  // When the backend returns requires_2fa: true, we park the email here so the
  // OTP screen can use it without the user having to re-type it.
  const [otpPending, setOtpPending] = useState(false);   // show OTP screen?
  const [otpEmail, setOtpEmail] = useState(null);         // email for verify call
  // otp_reason tells the UI WHY OTP was triggered so it can display the right
  // message: "FIRST_LOGIN" | "RISK_TRIGGERED" | "ADMIN_2FA" | null
  const [otpReason, setOtpReason] = useState(null);

  // ============================================
  // INITIAL AUTH CHECK
  // ============================================

  const checkAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const hasToken = !!tokenManager.getAccessToken();
      if (!hasToken) {
        setUser(null);
        setLoading(false);
        return;
      }

      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (err) {
      console.error('Auth check failed:', err);
      tokenManager.clearTokens();
      setUser(null);
      setError(err.userMessage || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================
  // LISTEN FOR AUTH EVENTS FROM AXIOS CLIENT
  // ============================================

  useEffect(() => {
    const handleTokenRefreshFailed = () => {
      setUser(null);
      setError('Session expired. Please login again.');
    };

    const handleLogoutEvent = () => {
      setUser(null);
      setError(null);
    };

    window.addEventListener('auth:token-refresh-failed', handleTokenRefreshFailed);
    window.addEventListener('auth:logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('auth:token-refresh-failed', handleTokenRefreshFailed);
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ============================================
  // LOGIN  (password → may trigger OTP gate)
  // ============================================

  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authAPI.login({
        email,
        password,
        deviceFingerprint: getOrCreateDeviceId(),
      });

      // ── OTP required ────────────────────────────────────────────────────────
      if (response.requires_2fa || response.otp_required) {
        setOtpEmail(email);
        setOtpReason(response.otp_reason || (response.otp_required ? 'FIRST_LOGIN' : null));
        setOtpPending(true);
        setLoading(false);
        return { success: true, requires_otp: true };
      }
      // ── Normal login success ────────────────────────────────────────────────
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      return { success: true, data: userData };

    } catch (err) {
      console.error('Login failed:', err);

      let errorMessage = 'Login failed. Please try again.';

      if (err.response?.status === 401) {
        errorMessage = err.response.data?.detail || 'Invalid email or password';
      } else if (err.response?.status === 423) {
        errorMessage = 'Account locked. Please reset your password.';
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // VERIFY OTP  (step-up — called from OTP screen)
  // ============================================

  const verifyOTP = async (otpCode) => {
    if (!otpEmail) {
      return { success: false, error: 'No pending OTP session. Please login again.' };
    }

    setLoading(true);
    setError(null);

    try {
      await authAPI.verifyLoginOTP({
        email: otpEmail,
        otpCode,
        deviceId: getOrCreateDeviceId(),
      });

      // Tokens are now stored — fetch full profile
      const userData = await authAPI.getCurrentUser();
      setUser(userData);

      // Clear OTP state
      setOtpPending(false);
      setOtpEmail(null);

      return { success: true, data: userData };

    } catch (err) {
      console.error('OTP verification failed:', err);

      let errorMessage = 'Invalid code. Please try again.';

      if (err.response?.status === 401) {
        errorMessage = 'Incorrect code. Please check and try again.';
      } else if (err.response?.status === 410) {
        errorMessage = 'Code expired. Please login again to get a new one.';
        // Reset so user goes back to login form
        setOtpPending(false);
        setOtpEmail(null);
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many attempts. Please wait before trying again.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Session expired. Please login again.';
        setOtpPending(false);
        setOtpEmail(null);
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RESEND OTP  (30-second cooldown gate)
  // ============================================

  const resendOTP = async () => {
    if (!otpEmail) {
      return { success: false, error: 'No pending OTP session. Please login again.' };
    }

    setError(null);

    try {
      const data = await authAPI.resendOTP(otpEmail);
      return { success: true, message: data.message };
    } catch (err) {
      let errorMessage = 'Failed to resend code. Please try again.';
      if (err.response?.status === 429) {
        errorMessage = err.response.data?.detail || 'Please wait before requesting a new code.';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // ============================================
  // CANCEL OTP  (user wants to go back to login)
  // ============================================

  const cancelOTP = () => {
    setOtpPending(false);
    setOtpEmail(null);
    setOtpReason(null);
    setError(null);
  };

  // ============================================
  // REGISTER
  // ============================================

  const register = async (email, password, fullName) => {
    setLoading(true);
    setError(null);

    try {
      await authAPI.register({ email, password, fullName });
      // Do NOT auto-login after registration — user must log in manually
      // so OTP verification is triggered on first login.
      return { success: true };
    } catch (err) {
      console.error('Registration failed:', err);

      let errorMessage = 'Registration failed. Please try again.';

      if (err.response?.status === 409) {
        errorMessage = 'Email already registered';
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many registration attempts. Please try again later.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response.data?.detail || 'Invalid registration data';
      } else if (err.userMessage) {
        errorMessage = err.userMessage;
      }

      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // LOGOUT
  // ============================================

  const logout = async () => {
    setLoading(true);
    setError(null);

    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout API call failed:', err);
    } finally {
      tokenManager.clearTokens();
      // M-5 FIX: sessionStorage.clear() wiped ALL sessionStorage including
      // URL-persisted filter params and any other app state stored by
      // non-auth code. tokenManager.clearTokens() already removes the three
      // auth-specific keys (access_token, refresh_token, token_expires_at).
      // No additional sessionStorage clearing is needed here.

      setUser(null);
      setError(null);
      setOtpPending(false);
      setOtpEmail(null);
      setOtpReason(null);
      setLoading(false);

      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  };

  // ============================================
  // CHANGE PASSWORD
  // ============================================

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);

    try {
      await authAPI.changePassword(currentPassword, newPassword);
      // Password changed successfully. Explicitly log out so the server
      // blacklists the current refresh token, then redirect to login.
      // Using setUser(null) alone was wrong: it nulled user state while
      // leaving valid tokens in storage, causing every subsequent
      // authenticated request (e.g. saving an address) to 401.
      await logoutUser();          // clears tokens + dispatches auth:logout
      setUser(null);
      return { success: true, message: 'Password changed successfully. Please login again.' };
    } catch (err) {
      console.error('Password change failed:', err);

      let errorMessage = 'Failed to change password';
      if (err.response?.status === 401) errorMessage = 'Current password is incorrect';
      else if (err.userMessage) errorMessage = err.userMessage;

      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PASSWORD RESET
  // ============================================

  const requestPasswordReset = async (email) => {
    setError(null);
    try {
      const response = await authAPI.requestPasswordReset(email);
      return { success: true, message: response.message };
    } catch (err) {
      const errorMessage = err.userMessage || 'Failed to send reset email';
      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    }
  };

  const confirmPasswordReset = async (token, newPassword) => {
    setError(null);
    try {
      await authAPI.confirmPasswordReset(token, newPassword);
      return { success: true, message: 'Password reset successfully. Please login.' };
    } catch (err) {
      let errorMessage = 'Failed to reset password';
      if (err.response?.status === 401) errorMessage = 'Invalid or expired reset token';
      else if (err.userMessage) errorMessage = err.userMessage;
      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    }
  };

  // ============================================
  // EMAIL VERIFICATION
  // ============================================

  const verifyEmail = async (token) => {
    setError(null);
    try {
      const response = await authAPI.verifyEmail(token);
      if (user) await checkAuth();
      return { success: true, message: response.message };
    } catch (err) {
      let errorMessage = 'Failed to verify email';
      if (err.response?.status === 401) errorMessage = 'Invalid or expired verification token';
      else if (err.userMessage) errorMessage = err.userMessage;
      setError(errorMessage);
      return { success: false, error: errorMessage, requestId: err.requestId };
    }
  };

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const isAuthenticated = !!user;
  // C-3 FIX: viewer and analyst are valid admin roles with full ROLE_PERMISSIONS
  // entries. The backend _ADMIN_ROLES now admits them through require_admin, and
  // require_permission() enforces their read-only grants on each route.
  // Excluding them here caused a silent lockout: the backend accepted their token
  // but the frontend redirected them away from /admin/* before any route was reached.
  const ADMIN_ROLES = new Set(['super_admin', 'admin', 'manager', 'viewer', 'analyst']);
  const isActive = user?.is_active !== false;
  // isAdmin requires both a valid role AND an active account. A suspended admin
  // is redirected by RequireAdmin (isActive check), but combining them here means
  // the full admin UI is never rendered for suspended accounts even briefly.
  const isAdmin = ADMIN_ROLES.has(user?.role) && isActive;
  const isVerified = user?.is_verified || false;

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // State
    user,
    loading,
    error,

    // OTP step-up
    otpPending,
    otpEmail,
    otpReason,

    // Computed
    isAuthenticated,
    isAdmin,
    isVerified,
    isActive,

    // Actions
    login,
    verifyOTP,
    cancelOTP,
    resendOTP,
    register,
    logout,
    changePassword,
    requestPasswordReset,
    confirmPasswordReset,
    verifyEmail,
    checkAuth,

    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export { AuthContext };